import { useTranslation } from '@packages/utils';
import { EEventType } from 'data/types/events.types';
import { TColumnType } from 'components/Base/Table/Table.types';
import useGetTableColumnData from 'components/Base/Table/components/SearchFilter';
import SessionDates from 'pages/private/Workshop/components/WorkshopList/components/SessionDates';
import { CopyTextButton } from 'components/Base/CopyTextButton';
import {
  EEventParentsFilterParams,
  TEventParentDataUI,
} from 'data/types/eventParents.types';
import Tags from 'components/Shared/Tags';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { ETagsUrls } from 'data/urls/tags.url';
import { ETagType } from 'data/types/tags.types';
import { eventParentsApi } from 'redux/apiSlice/eventParents.slice';
import AttendanceColumn from './AttendanceColumn';
import EventFamilyListActions from './EventFamilyListActions';

const eventFamilyListKey = 'descriptions.admin-portal.family.event-family-list';

const useGetEventFamilyColumnsData = () => {
  const { getTableColumnSearchProps } = useGetTableColumnData();

  const { t } = useTranslation({
    keyPrefix: eventFamilyListKey.concat('.table.title'),
  });
  const { t: tEnum } = useTranslation({
    keyPrefix: 'enums',
  });

  const isTagColumnEditable = useCheckPermission('EVENT_TAG_EDIT');

  const eventFamilyColumns: TColumnType<TEventParentDataUI>[] = [
    {
      title: t('REGISTRATION_DATE', 'Registration Date'),
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      sorter: true,
      width: 150,
    },
    {
      title: t('FIRST_NAME', 'First Name'),
      dataIndex: 'firstName',
      key: EEventParentsFilterParams.FIRST_NAME,
      sorter: true,
      ...getTableColumnSearchProps<TEventParentDataUI>(),
    },
    {
      title: t('LAST_NAME', 'Last Name'),
      dataIndex: 'lastName',
      key: EEventParentsFilterParams.LAST_NAME,
      sorter: true,
      ...getTableColumnSearchProps<TEventParentDataUI>(),
    },
    {
      title: t('MOBILE', 'Mobile'),
      dataIndex: 'phone',
      key: EEventParentsFilterParams.MOBILE,
      ellipsis: true,
      ...getTableColumnSearchProps<TEventParentDataUI>('number'),
    },
    {
      title: t('EMAIL', 'Email'),
      dataIndex: 'email',
      key: EEventParentsFilterParams.EMAIL,
      ellipsis: true,
      onCell: () => ({
        className: 'custom-cell',
        onClick: e => {
          e.stopPropagation();
        },
      }),
      render: (_, record) => {
        return <CopyTextButton title={record.email} />;
      },
      ...getTableColumnSearchProps<TEventParentDataUI>(),
    },
    {
      title: t('EVENT_LOCATION', 'Event Location'),
      dataIndex: 'location',
      key: EEventParentsFilterParams.LOCATION,
      ellipsis: true,
      filters: [],
      filterSearch: true,
    },
    {
      title: t('EVENT_TYPE', 'Event Type'),
      dataIndex: 'eventType',
      key: EEventParentsFilterParams.TYPE,
      ellipsis: true,
      filters: Object.values(EEventType).map(type => ({
        text: tEnum(`event-type.${type}`, type),
        value: type,
      })),
      render: (_, record) => (
        <>{tEnum(`event-type.${record.eventType}`, record.eventType)}</>
      ),
    },
    {
      title: t('TITLE', 'Title'),
      dataIndex: 'eventTitle',
      key: EEventParentsFilterParams.TITLE,
      ellipsis: true,
      ...getTableColumnSearchProps<TEventParentDataUI>(),
    },
    {
      title: t('HOST', 'Host'),
      dataIndex: 'host',
      key: EEventParentsFilterParams.HOST,
      ellipsis: true,
      filters: [],
      filterSearch: true,
      render: (_, record) =>
        record.host ? tEnum(`host-type.${record.host}`, record.host) : null,
    },
    {
      title: t('EVENT_DATE', 'Event Date'),
      dataIndex: 'sessionDates',
      key: 'sessionDates',
      ellipsis: true,
      render: sessionDates => <SessionDates sessionDates={sessionDates} />,
    },
    {
      title: t('ATTENDANCE', 'Attendance'),
      dataIndex: 'attendances',
      key: EEventParentsFilterParams.ATTENDANCE,
      width: 120,
      align: 'center' as const,
      filters: [
        { text: 'Attended', value: 'true' },
        { text: 'Not Attended', value: 'false' },
      ],
      filterMultiple: false,
      render: (_, record) => (
        <AttendanceColumn attendances={record.attendances} />
      ),
    },
    {
      title: t('TAG', 'Tag'),
      dataIndex: 'tags',
      key: 'tagIds',
      filters: [],
      filterSearch: true,
      width: 50,
      onCell: () => ({
        className: 'custom-cell',
        onClick: e => {
          e.stopPropagation();
        },
      }),
      render: (_, record) => (
        <Tags
          selectedTags={record.tags || []}
          associationId={record.registrantId}
          url={ETagsUrls.EVENT_REGISTRANT}
          invalidateTagsConfig={[
            { invalidateTag: 'event-parents', sliceName: eventParentsApi },
          ]}
          tagType={ETagType.REGISTRANT}
          isEditable={isTagColumnEditable}
          isAddDisabled
        />
      ),
    },
    {
      title: ' ',
      key: 'actions',
      dataIndex: 'actions',
      fixed: 'right',
      width: 60,
      render: (_, record) => <EventFamilyListActions familyData={record} />,
      onCell: () => ({
        onClick: event => {
          event.stopPropagation();
        },
      }),
    },
  ];

  return { eventFamilyColumns };
};

export default useGetEventFamilyColumnsData;
